import React, { useEffect, useState, useMemo } from 'react';
import { ComposableMap, Geographies, Geography, Marker, Line } from 'react-simple-maps';
import { supabase } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { airportCoordinates } from '@/data/airportCoordinates';

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

function seededRandom(seed) {
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

export default function MapPage() {
  const [routes, setRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRoutes();
  }, []);

  const fetchRoutes = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/home?auth_mode=login');
        return;
      }

      const response = await fetch('/api/admin/route-map', {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        if (response.status === 403 || response.status === 401) {
          throw new Error('Unauthorized access');
        }
        throw new Error('Failed to fetch map data');
      }

      const data = await response.json();
      setRoutes(data.routes || []);
    } catch (err) {
      console.error('Map fetch error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const processedRoutes = useMemo(() => {
    return routes.map((route, i) => {
      const from = airportCoordinates[route.origin];
      const to = airportCoordinates[route.destination];
      if (!from || !to) return null;
      const duration = 1.2 + seededRandom(i * 7) * 1.5;
      const delay = seededRandom(i * 13) * 4;
      return { from, to, origin: route.origin, destination: route.destination, duration, delay, idx: i };
    }).filter(Boolean);
  }, [routes]);

  const airports = useMemo(() => {
    const map = new Map();
    processedRoutes.forEach(r => {
      const fk = r.from.join(',');
      const tk = r.to.join(',');
      if (!map.has(fk)) map.set(fk, { coords: r.from, code: r.origin });
      if (!map.has(tk)) map.set(tk, { coords: r.to, code: r.destination });
    });
    return Array.from(map.values());
  }, [processedRoutes]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white/20 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500/50 mx-auto mb-4" />
          <h2 className="text-xl font-light mb-2">Access Denied</h2>
          <p className="text-white/60 mb-6">{error}</p>
          <Button variant="outline" onClick={() => navigate('/dashboard')} className="border-white/10 hover:bg-white/5">
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] text-white relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 z-10 p-6 flex justify-between items-center pointer-events-none">
        <div className="pointer-events-auto">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="text-white/40 hover:text-white hover:bg-white/10 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </div>
        <div className="text-right">
          <h1 className="text-2xl font-light tracking-tight">Where our customers are going</h1>
        </div>
      </div>

      <div className="w-full h-screen flex items-center justify-center">
        <ComposableMap
          projection="geoEqualEarth"
          projectionConfig={{ scale: 180 }}
          style={{ width: "100%", height: "100%" }}
        >
          <defs>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2.5" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>

          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#0a0a0a"
                  stroke="#1a1a1a"
                  strokeWidth={0.4}
                  style={{
                    default: { outline: "none" },
                    hover: { outline: "none" },
                    pressed: { outline: "none" },
                  }}
                />
              ))
            }
          </Geographies>

          {processedRoutes.map((route) => (
            <Line
              key={`base-${route.idx}`}
              from={route.from}
              to={route.to}
              stroke="#ffffff"
              strokeWidth={0.3}
              strokeLinecap="round"
              style={{ opacity: 0.06 }}
            />
          ))}

          {processedRoutes.map((route) => (
            <Line
              key={`pulse-${route.idx}`}
              from={route.from}
              to={route.to}
              stroke="#ffffff"
              strokeWidth={1.5}
              strokeLinecap="round"
              style={{
                strokeDasharray: '5 95',
                animation: `travel ${route.duration}s linear ${route.delay}s infinite`,
                filter: 'url(#glow)',
                opacity: 0.85
              }}
            />
          ))}

          {airports.map((airport, i) => (
            <Marker key={i} coordinates={airport.coords}>
              <circle r={1.5} fill="#ffffff" fillOpacity={0.85} />
              <circle r={4} fill="#ffffff" fillOpacity={0.04} />
            </Marker>
          ))}
        </ComposableMap>
      </div>

      <style>{`
        @keyframes travel {
          from { stroke-dashoffset: 100; }
          to   { stroke-dashoffset: 0; }
        }
      `}</style>
    </div>
  );
}
